import { getMarketplaces } from 'APIs/marketplaces/marketplaces';
import { Marketplace } from 'APIs/marketplaces/marketplaces.types';
import HeaderTitle from 'Components/HeaderTitle/HeaderTitle';
import CreateMarketplace from 'Components/Marketplaces/CreateMarketplace';
import { Content, LpBox } from 'Constants/styles';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import getErrorMessage from 'utils/getErrorMessage';

import MarketplaceDataTable from './MarketplaceDataTable';
import { AddNewMarketplaceButton, MarketplacesHeader } from './styles';

const Marketplaces = (): ReactElement => {
  const { apiBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
  }));

  const [marketplaces, setMarketplaces] = useState<Array<Marketplace>>([]);
  const [activePage, setActivePage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchingError, setFetchingError] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);

  const fetchMarketplaces = useCallback(async () => {
    if (!apiBaseUri) {
      return;
    }

    try {
      setIsLoading(true);

      const resp = await getMarketplaces(apiBaseUri, {
        page: activePage,
        limit: perPage,
        sort: '-createdAt',
      });
      setMarketplaces(resp.items);
      setTotalCount(resp.totalRecords);
    } catch (e) {
      setMarketplaces([]);
      setTotalCount(0);
      setFetchingError(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [activePage, apiBaseUri, perPage]);

  const handlePerPageChange = useCallback((newPerPage: number, page: number) => {
    setPerPage(newPerPage);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setActivePage(page);
  }, []);

  useEffect(() => {
    fetchMarketplaces();
  }, [activePage, perPage, fetchMarketplaces]);

  const postCreateMarketplace = useCallback(() => {
    fetchMarketplaces();
  }, [fetchMarketplaces]);

  return (
    <div className="mb-4">
      <HeaderTitle title="Marketplaces" />
      <Content>
        <LpBox>
          <div>
            <MarketplacesHeader>
              <h3 className="mb-0">Marketplaces</h3>
              <AddNewMarketplaceButton
                onClick={() => {
                  setShowModal(true);
                }}
              >
                Add new marketplace
              </AddNewMarketplaceButton>
            </MarketplacesHeader>

            <CreateMarketplace
              showModal={showModal}
              setShowModal={setShowModal}
              postCreateMarketplace={postCreateMarketplace}
            />
            <MarketplaceDataTable
              marketplaceList={marketplaces}
              totalRows={totalCount}
              handlePageChange={handlePageChange}
              handlePerRowsChange={handlePerPageChange}
              fetchListLoader={isLoading}
              limit={perPage}
              activePage={activePage}
              isErrorRequest={!!fetchingError}
            />
          </div>
        </LpBox>
      </Content>
    </div>
  );
};

export default Marketplaces;
